import { useEffect, useRef, type Dispatch } from 'react';

import type { Locale } from '../../i18n-module/locale.js';
import type { CancelAction } from '../state-machine-feature/cancel-action.type.js';
import { useTenantConfig } from '../../tenant-config-module/use-tenant-config.js';
import { formatAppointment } from '../../token-module/format-appointment.js';
import { isWithinCancelCutoff } from '../../token-module/check-cutoff.js';
import { useTokenFromRoute } from '../../token-module/use-token-from-route.js';

/**
 * Optionen für `useCancelRouteBootstrap`. `now` und `locale` sind
 * Pflicht-Injektionen — `locale` kommt aus i18next, `now` aus `Date.now()`
 * (Tests setzen einen festen Wert).
 */
interface UseCancelRouteBootstrapOptions {
  readonly dispatch: Dispatch<CancelAction>;
  readonly locale: Locale;
  readonly now: number;
}

/**
 * Liest den Token aus der Route, prüft Format und 24-h-Cutoff und dispatched
 * genau einmal pro `[token, locale]`-Identität die passende State-Machine-
 * Aktion:
 *   - Token kaputt → `TOKEN_INVALID`.
 *   - Cutoff verletzt → `CUTOFF_REACHED`.
 *   - Sonst → `TOKEN_PARSED` mit formatierter Termin-Info.
 *
 * `now` wird bewusst nur beim ersten Effekt-Lauf gelesen — die 24-h-Grenze
 * soll nicht mitten in einem laufenden UX-Schritt von `false` auf `true`
 * kippen. Locale-Wechsel hingegen löst eine Re-Formatierung aus (DE/EN-Switch
 * nach Token-Parsing).
 */
export function useCancelRouteBootstrap(options: UseCancelRouteBootstrapOptions): void {
  const { dispatch, locale } = options;
  const nowRef = useRef(options.now);
  const tokenResult = useTokenFromRoute({ nowMs: nowRef.current });
  const tenantConfig = useTenantConfig();

  useEffect(() => {
    if (tokenResult.status === 'error') {
      dispatch({ type: 'TOKEN_INVALID' });
      return;
    }
    if (!isWithinCancelCutoff(tokenResult.decoded, nowRef.current)) {
      dispatch({ type: 'CUTOFF_REACHED' });
      return;
    }
    dispatch({
      type: 'TOKEN_PARSED',
      appointmentInfo: formatAppointment(tokenResult.decoded, locale, tenantConfig.locations),
    });
  }, [tokenResult, locale, dispatch, tenantConfig.locations]);
}
